import { createSignal, onMount, For, Show, createMemo, createEffect } from "solid-js";
import { api, type Project, type User, type ProjectMember, type Organization, type TrackingSettings } from "../shared";
import { managedJoinUrl } from "../runtime";

const PAGE_SIZE = 20;
const PERM_LABELS: Record<string, string> = {
  command: "Command Center",
  manage_members: "Manage Members",
  edit_project: "Edit Project",
  view_blocks: "View Blocks",
};

export function ProjectsPanel() {
  const PROJECT_SEGMENT_PATTERN = /^[a-z0-9]+$/;
  const normalizeSegment = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
  const projectCacheStatus = (project: Project) => {
    const generatedAt = project.blocksCacheGeneratedAt ? Date.parse(project.blocksCacheGeneratedAt) : NaN;
    const expiresAt = project.blocksCacheExpiresAt ? Date.parse(project.blocksCacheExpiresAt) : NaN;
    const invalidatedAt = project.blocksCacheInvalidatedAt ? Date.parse(project.blocksCacheInvalidatedAt) : NaN;
    if (!project.blocksCacheGeneratedAt) return "missing";
    if (project.blocksCacheInvalidatedAt && (!Number.isFinite(generatedAt) || invalidatedAt >= generatedAt)) return "invalidated";
    if (project.blocksCacheExpiresAt && Number.isFinite(expiresAt) && expiresAt <= Date.now()) return "stale";
    return "fresh";
  };
  const [projects, setProjects] = createSignal<Project[]>([]);
  const [trackingSettings, setTrackingSettings] = createSignal<TrackingSettings>({ distanceThresholdMeters: 0, timeThresholdSeconds: 0, timeThresholdBehavior: "live_only", blocksCacheTtlSeconds: 86400 });
  const [users, setUsers] = createSignal<User[]>([]);
  const [orgs, setOrgs] = createSignal<Organization[]>([]);
  const [createOrgUsers, setCreateOrgUsers] = createSignal<User[]>([]);
  const [page, setPage] = createSignal(1);
  const [error, setError] = createSignal("");
  const [filterUserId, setFilterUserId] = createSignal("");
  const [expandedProject, setExpandedProject] = createSignal<string | null>(null);
  const [members, setMembers] = createSignal<Record<string, ProjectMember[]>>({});
  const [loadingMembers, setLoadingMembers] = createSignal(false);
  const [editMember, setEditMember] = createSignal<ProjectMember | null>(null);
  const [editRole, setEditRole] = createSignal("");
  const [editPerms, setEditPerms] = createSignal<Record<string, boolean>>({});
  const [showCreate, setShowCreate] = createSignal(false);
  const [createName, setCreateName] = createSignal("");
  const [createDescription, setCreateDescription] = createSignal("");
  const [createSlug, setCreateSlug] = createSignal("");
  const [createVisibility, setCreateVisibility] = createSignal("private");
  const [createBlockSize, setCreateBlockSize] = createSignal(50);
  const [createAccessMode, setCreateAccessMode] = createSignal("open");
  const [createTrackingOverrideEnabled, setCreateTrackingOverrideEnabled] = createSignal(false);
  const [createTrackingDistance, setCreateTrackingDistance] = createSignal(0);
  const [createTrackingTime, setCreateTrackingTime] = createSignal(0);
  const [createTrackingBehavior, setCreateTrackingBehavior] = createSignal<TrackingSettings["timeThresholdBehavior"]>("live_only");
  const [createOwnerId, setCreateOwnerId] = createSignal("");
  const [createOrganizationId, setCreateOrganizationId] = createSignal("");
  const [creating, setCreating] = createSignal(false);
  const [showEdit, setShowEdit] = createSignal(false);
  const [editProject, setEditProject] = createSignal<Project | null>(null);
  const [editName, setEditName] = createSignal("");
  const [editDescription, setEditDescription] = createSignal("");
  const [editSlug, setEditSlug] = createSignal("");
  const [editLifecycle, setEditLifecycle] = createSignal("");
  const [editVisibility, setEditVisibility] = createSignal("");
  const [editAccessMode, setEditAccessMode] = createSignal("");
  const [editBlockSize, setEditBlockSize] = createSignal(50);
  const [editTrackingOverrideEnabled, setEditTrackingOverrideEnabled] = createSignal(false);
  const [editTrackingDistance, setEditTrackingDistance] = createSignal(0);
  const [editTrackingTime, setEditTrackingTime] = createSignal(0);
  const [editTrackingBehavior, setEditTrackingBehavior] = createSignal<TrackingSettings["timeThresholdBehavior"]>("live_only");
  const [editing, setEditing] = createSignal(false);
  const [savingTrackingSettings, setSavingTrackingSettings] = createSignal(false);
  const [showAddMember, setShowAddMember] = createSignal(false);
  const [addMemberProjectId, setAddMemberProjectId] = createSignal("");
  const [addMemberUserId, setAddMemberUserId] = createSignal("");
  const [addMemberRole, setAddMemberRole] = createSignal("manager");
  const [showInvite, setShowInvite] = createSignal(false);
  const [inviteProjectId, setInviteProjectId] = createSignal("");
  const [inviteEmail, setInviteEmail] = createSignal("");
  const [inviteRole, setInviteRole] = createSignal("searcher");
  const [inviteResult, setInviteResult] = createSignal<{ link: string } | null>(null);
  const [projectInvites, setProjectInvites] = createSignal<Record<string, any[]>>({});
  const [showInvitesProject, setShowInvitesProject] = createSignal<string | null>(null);

  const totalPages = createMemo(() => Math.max(1, Math.ceil((projects() || []).length / PAGE_SIZE)));
  const selectedCreateOrg = createMemo(() => orgs().find((org) => org.id === createOrganizationId()) ?? null);
  const createProjectFullSlug = createMemo(() => {
    const prefix = selectedCreateOrg()?.slug;
    const segment = normalizeSegment(createSlug()) || "project";
    return prefix ? `${prefix}-${segment}` : segment;
  });
  const editProjectPrefix = createMemo(() => editProject()?.organizationSlug ?? null);
  const editProjectFullSlug = createMemo(() => {
    const segment = normalizeSegment(editSlug()) || "project";
    return editProjectPrefix() ? `${editProjectPrefix()}-${segment}` : segment;
  });
  const pageProjects = createMemo(() => {
    const start = (page() - 1) * PAGE_SIZE;
    return (projects() || []).slice(start, start + PAGE_SIZE);
  });
  const eligibleCreateOwners = createMemo(() => {
    const source = createOrganizationId() ? createOrgUsers() : users();
    return source.filter((user) => {
      const role = ((user as any).role ?? "").toLowerCase();
      return role === "" || role === "owner" || role === "admin" || role === "manager";
    });
  });

  async function loadProjects() {
    try {
      const filters: { userId?: string } = {};
      const uid = filterUserId();
      if (uid) filters.userId = uid;
      setProjects(await api.projects.list(filters));
      setPage(1);
      setError("");
    } catch (e: any) {
      setError(e.message);
    }
  }

  async function loadUsers() {
    try { setUsers(await api.users.list()); } catch {}
  }

  async function loadOrgs() {
    try { setOrgs(await api.organizations.list()); } catch {}
  }

  onMount(() => { loadProjects(); loadUsers(); loadOrgs(); loadTrackingSettings(); });

  async function loadTrackingSettings() {
    try {
      setTrackingSettings(await api.settings.tracking());
    } catch {}
  }

  createEffect(() => {
    const orgId = createOrganizationId();
    if (!orgId) {
      setCreateOrgUsers([]);
      return;
    }
    api.organizations.orgUsers(orgId)
      .then((result) => setCreateOrgUsers(result))
      .catch(() => setCreateOrgUsers([]));
  });

  function handleUserFilter(userId: string) {
    setFilterUserId(userId);
    loadProjects();
  }

  async function toggleProject(id: string) {
    if (expandedProject() === id) {
      setExpandedProject(null);
      return;
    }
    setExpandedProject(id);
    if (!members()[id]) {
      setLoadingMembers(true);
      try {
        const m = await api.projects.members(id);
        setMembers((p) => ({ ...p, [id]: m }));
      } catch (e: any) { setError(e.message); }
      setLoadingMembers(false);
    }
  }

  function startEditMember(m: ProjectMember) {
    setEditMember(m);
    setEditRole(m.role);
    setEditPerms({ ...(m.permissions ?? {}) });
  }

  async function saveMember() {
    const m = editMember();
    if (!m) return;
    try {
      await api.projects.updateMember(expandedProject()!, m.userId, {
        role: editRole() !== m.role ? editRole() : undefined,
        permissions: editPerms(),
      });
      setEditMember(null);
      const updated = await api.projects.members(expandedProject()!);
      setMembers((p) => ({ ...p, [expandedProject()!]: updated }));
    } catch (e: any) { setError(e.message); }
  }

  async function doCreateProject() {
    if (!createName()) return;
    const projectSlug = normalizeSegment(createSlug());
    if (!PROJECT_SEGMENT_PATTERN.test(projectSlug)) {
      setError("Project slug must use only lowercase letters and numbers.");
      return;
    }
    setCreating(true);
    try {
      await api.projects.create({
        name: createName(),
        description: createDescription(),
        slug: projectSlug,
        visibility: createVisibility(),
        blockSize: createBlockSize(),
        accessMode: createAccessMode(),
        trackingThresholdOverrideEnabled: createTrackingOverrideEnabled(),
        trackingDistanceThresholdMeters: createTrackingOverrideEnabled() ? createTrackingDistance() : null,
        trackingTimeThresholdSeconds: createTrackingOverrideEnabled() ? createTrackingTime() : null,
        trackingTimeThresholdBehavior: createTrackingOverrideEnabled() ? createTrackingBehavior() : null,
        ownerId: createOwnerId() || undefined,
        organizationId: createOrganizationId() || undefined,
      });
      setShowCreate(false);
      setCreateName(""); setCreateDescription(""); setCreateVisibility("private");
      setCreateSlug("");
      setCreateBlockSize(50); setCreateAccessMode("open"); setCreateOwnerId(""); setCreateOrganizationId("");
      setCreateTrackingOverrideEnabled(false); setCreateTrackingDistance(0); setCreateTrackingTime(0); setCreateTrackingBehavior("live_only");
      loadProjects();
    } catch (e: any) { setError(e.message); }
    setCreating(false);
  }

  function startEditProject(p: Project) {
    setEditProject(p);
    setEditName(p.name);
    setEditDescription(p.description ?? "");
    setEditSlug((p as any).slug ?? "");
    setEditLifecycle(p.lifecycle);
    setEditVisibility(p.visibility);
    setEditAccessMode((p as any).accessMode ?? "open");
    setEditBlockSize(p.blockSize);
    setEditTrackingOverrideEnabled(!!(p as any).trackingThresholdOverrideEnabled);
    setEditTrackingDistance((p as any).trackingDistanceThresholdMeters ?? 0);
    setEditTrackingTime((p as any).trackingTimeThresholdSeconds ?? 0);
    setEditTrackingBehavior((p as any).trackingTimeThresholdBehavior ?? "live_only");
    setShowEdit(true);
  }

  async function doUpdateProject() {
    const p = editProject();
    if (!p) return;
    const projectSlug = normalizeSegment(editSlug());
    if (!PROJECT_SEGMENT_PATTERN.test(projectSlug)) {
      setError("Project slug must use only lowercase letters and numbers.");
      return;
    }
    setEditing(true);
    try {
      await api.projects.update(p.id, {
        name: editName() !== p.name ? editName() : undefined,
        description: editDescription() !== (p.description ?? "") ? editDescription() : undefined,
        slug: projectSlug !== ((p as any).slug ?? "") ? projectSlug : undefined,
        lifecycle: editLifecycle() !== p.lifecycle ? editLifecycle() : undefined,
        visibility: editVisibility() !== p.visibility ? editVisibility() : undefined,
        accessMode: editAccessMode() !== ((p as any).accessMode ?? "open") ? editAccessMode() : undefined,
        blockSize: editBlockSize() !== p.blockSize ? editBlockSize() : undefined,
        trackingThresholdOverrideEnabled: editTrackingOverrideEnabled() !== !!(p as any).trackingThresholdOverrideEnabled ? editTrackingOverrideEnabled() : undefined,
        trackingDistanceThresholdMeters: editTrackingOverrideEnabled() ? editTrackingDistance() : null,
        trackingTimeThresholdSeconds: editTrackingOverrideEnabled() ? editTrackingTime() : null,
        trackingTimeThresholdBehavior: editTrackingOverrideEnabled() ? editTrackingBehavior() : null,
      });
      setShowEdit(false);
      loadProjects();
    } catch (e: any) { setError(e.message); }
    setEditing(false);
  }

  async function doAddMember() {
    if (!addMemberUserId() || !addMemberProjectId()) return;
    try {
      await api.projects.addMember(addMemberProjectId(), addMemberUserId(), addMemberRole());
      setShowAddMember(false);
      const updated = await api.projects.members(addMemberProjectId());
      setMembers((p) => ({ ...p, [addMemberProjectId()]: updated }));
    } catch (e: any) { setError(e.message); }
  }

  async function doRemoveProjectMember(projectId: string, userId: string) {
    try {
      await api.projects.removeMember(projectId, userId);
      const updated = await api.projects.members(projectId);
      setMembers((p) => ({ ...p, [projectId]: updated }));
    } catch (e: any) { setError(e.message); }
  }

  async function doCreateInvite() {
    if (!inviteEmail() || !inviteProjectId()) return;
    try {
      const result = await api.projects.createInvite(inviteProjectId(), { email: inviteEmail(), role: inviteRole() });
      setInviteResult({ link: managedJoinUrl(result.token) });
      setInviteEmail("");
    } catch (e: any) { setError(e.message); }
  }

  async function doRemoveInvite(projectId: string, inviteId: string) {
    try {
      await api.projects.removeInvite(projectId, inviteId);
      const invites = await api.projects.listInvites(projectId);
      setProjectInvites((p) => ({ ...p, [projectId]: invites }));
    } catch (e: any) { setError(e.message); }
  }

  async function toggleInvites(projectId: string) {
    if (showInvitesProject() === projectId) { setShowInvitesProject(null); return; }
    setShowInvitesProject(projectId);
    if (!projectInvites()[projectId]) {
      try {
        const invites = await api.projects.listInvites(projectId);
        setProjectInvites((p) => ({ ...p, [projectId]: invites }));
      } catch (e: any) { setError(e.message); }
    }
  }

  async function saveTrackingSettings() {
    setSavingTrackingSettings(true);
    try {
      const result = await api.settings.updateTracking(trackingSettings());
      setTrackingSettings(result.settings);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingTrackingSettings(false);
    }
  }

  async function invalidateProjectBlockCache(projectId: string) {
    try {
      await api.projects.invalidateBlockCache(projectId);
      await loadProjects();
    } catch (e: any) {
      setError(e.message);
    }
  }

  return (
    <div class="space-y-6">
      <Show when={error()}>
        <div class="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm flex items-center justify-between">
          <span>{error()}</span>
          <button onClick={() => setError("")} class="text-gray-500 hover:text-gray-300 ml-4 cursor-pointer">✕</button>
        </div>
      </Show>

      <div class="flex items-center justify-between flex-wrap gap-3">
        <h2 class="text-lg font-semibold">Projects ({projects().length})</h2>
        <div class="flex items-center gap-3">
          <select value={filterUserId()} onChange={(e) => handleUserFilter(e.currentTarget.value)} class="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer max-w-56 truncate">
            <option value="">All users</option>
            <For each={users()}>{(u) => <option value={u.id}>{u.email} ({u.role})</option>}</For>
          </select>
          <button onClick={() => setShowCreate(true)} class="px-4 py-2 bg-cyan-600 text-white rounded-lg text-sm font-medium hover:bg-cyan-500 transition-colors cursor-pointer">+ Create Project</button>
        </div>
      </div>

      <section class="rounded-lg border border-gray-800 bg-gray-900 p-4 space-y-4">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h3 class="text-sm font-semibold text-gray-200">Platform Tracking Defaults</h3>
            <p class="text-xs text-gray-500">Persist a new path sample when distance or time threshold is exceeded. Use 0 to disable either threshold.</p>
          </div>
          <button onClick={saveTrackingSettings} disabled={savingTrackingSettings()} class="rounded bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">
            {savingTrackingSettings() ? "Saving..." : "Save Defaults"}
          </button>
        </div>
        <div class="grid gap-4 md:grid-cols-4">
          <label class="space-y-1 text-sm text-gray-300">
            <span class="font-medium">Distance threshold (m)</span>
            <input type="number" min="0" step="0.1" value={trackingSettings().distanceThresholdMeters} onInput={(e) => setTrackingSettings((current) => ({ ...current, distanceThresholdMeters: parseFloat(e.currentTarget.value) || 0 }))} class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-200" />
          </label>
          <label class="space-y-1 text-sm text-gray-300">
            <span class="font-medium">Time threshold (s)</span>
            <input type="number" min="0" step="5" value={trackingSettings().timeThresholdSeconds} onInput={(e) => setTrackingSettings((current) => ({ ...current, timeThresholdSeconds: parseInt(e.currentTarget.value) || 0 }))} class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-200" />
          </label>
          <label class="space-y-1 text-sm text-gray-300">
            <span class="font-medium">Time threshold behavior</span>
            <select value={trackingSettings().timeThresholdBehavior} onChange={(e) => setTrackingSettings((current) => ({ ...current, timeThresholdBehavior: e.currentTarget.value as TrackingSettings["timeThresholdBehavior"] }))} class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-200">
              <option value="live_only">Live update only</option>
              <option value="persist_and_live">Persist and live update</option>
              <option value="no_update">No update</option>
            </select>
          </label>
          <label class="space-y-1 text-sm text-gray-300">
            <span class="font-medium">Blocks cache TTL (s)</span>
            <input type="number" min="0" step="60" value={trackingSettings().blocksCacheTtlSeconds} onInput={(e) => setTrackingSettings((current) => ({ ...current, blocksCacheTtlSeconds: Math.max(0, parseInt(e.currentTarget.value, 10) || 0) }))} class="w-full rounded border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-200" />
          </label>
        </div>
      </section>

      <div class="border border-gray-800 rounded-lg overflow-x-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-900 text-gray-400">
            <tr>
              <th class="text-left px-4 py-3 font-medium w-8"></th>
              <th class="text-left px-4 py-3 font-medium">Name</th>
              <th class="text-left px-4 py-3 font-medium">Lifecycle</th>
              <th class="text-left px-4 py-3 font-medium">Visibility</th>
              <th class="text-left px-4 py-3 font-medium">Created By</th>
              <th class="text-center px-4 py-3 font-medium">Members</th>
              <th class="text-center px-4 py-3 font-medium">Blocks</th>
              <th class="text-left px-4 py-3 font-medium">Cache</th>
              <th class="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800">
            <For each={pageProjects()}>
              {(p) => (
                <>
                  <tr class="hover:bg-gray-900/50 transition-colors cursor-pointer" onClick={() => toggleProject(p.id)}>
                    <td class="px-4 py-3 text-gray-600">{expandedProject() === p.id ? "▼" : "▶"}</td>
                    <td class="px-4 py-3 text-gray-200 font-medium">
                      <div>{p.name}</div>
                      <div class="text-[11px] text-gray-500 font-mono">{p.slug}{p.fullSlug ? ` (${p.fullSlug})` : ""}</div>
                    </td>
                    <td class="px-4 py-3"><LifecycleBadge lifecycle={p.lifecycle} /></td>
                    <td class="px-4 py-3 text-gray-400 text-xs">{p.visibility}</td>
                    <td class="px-4 py-3 text-gray-300 text-xs">{p.ownerEmail ?? "-"}</td>
                    <td class="px-4 py-3 text-gray-300 text-center">{p.memberCount}</td>
                    <td class="px-4 py-3 text-gray-300 text-center">{p.blockCount}</td>
                    <td class="px-4 py-3 text-xs">
                      <div class="text-[10px] uppercase tracking-wide text-gray-500">project_blocks</div>
                      <div class="text-gray-300 capitalize">{projectCacheStatus(p)}</div>
                      <div class="text-[11px] text-gray-500">{p.blocksCacheGeneratedAt ? p.blocksCacheGeneratedAt.slice(0, 19).replace("T", " ") : "Not built"}</div>
                    </td>
                    <td class="px-4 py-3 text-right space-x-1">
                      <button onClick={(e) => { e.stopPropagation(); startEditProject(p); }} class="text-xs px-2 py-0.5 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 cursor-pointer">Edit</button>
                      <button onClick={(e) => { e.stopPropagation(); void invalidateProjectBlockCache(p.id); }} class="text-xs px-2 py-0.5 bg-amber-900/30 border border-amber-700 text-amber-300 rounded hover:bg-amber-900/50 cursor-pointer">Invalidate Cache</button>
                    </td>
                  </tr>
                  <Show when={expandedProject() === p.id}>
                    <tr>
                      <td colspan="9" class="bg-gray-950 p-0">
                        <div class="px-4 py-3 border-t border-gray-800">
                          <h4 class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Members 
                            <button onClick={(e) => { e.stopPropagation(); setAddMemberProjectId(p.id); setAddMemberUserId(""); setAddMemberRole("manager"); setShowAddMember(true); }} class="ml-2 text-xs px-2 py-0.5 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded hover:bg-cyan-900/50 cursor-pointer">+ Add</button>
                            <button onClick={(e) => { e.stopPropagation(); setInviteProjectId(p.id); setInviteEmail(""); setInviteRole("searcher"); setInviteResult(null); setShowInvite(true); }} class="ml-1 text-xs px-2 py-0.5 bg-purple-900/30 border border-purple-700 text-purple-300 rounded hover:bg-purple-900/50 cursor-pointer">+ Invite</button>
                            <button onClick={(e) => { e.stopPropagation(); toggleInvites(p.id); }} class="ml-1 text-xs px-2 py-0.5 bg-gray-800 text-gray-400 rounded hover:text-gray-200 cursor-pointer">{showInvitesProject() === p.id ? "Hide Invites" : "Invites"}</button>
                          </h4>
                          <Show when={!loadingMembers()} fallback={<div class="text-gray-500 text-xs py-2">Loading...</div>}>
                            <Show when={members()[p.id]} fallback={<div class="text-gray-600 text-xs py-2">No members or failed to load</div>}>
                              <table class="w-full text-xs">
                                <thead class="text-gray-500">
                                  <tr>
                                    <th class="text-left py-1 font-medium">User</th>
                                    <th class="text-left py-1 font-medium">Role</th>
                                    <th class="text-left py-1 font-medium">Status</th>
                                    <th class="text-left py-1 font-medium">Permissions</th>
                                    <th class="text-right py-1 font-medium">Action</th>
                                  </tr>
                                </thead>
                                <tbody class="divide-y divide-gray-800/50">
                                  <For each={members()[p.id]}>
                                    {(m) => (
                                      <tr>
                                        <td class="py-1.5 text-gray-200">{m.userEmail}</td>
                                        <td class="py-1.5"><RoleBadge role={m.role} /></td>
                                        <td class="py-1.5"><StatusBadge status={m.status} /></td>
                                        <td class="py-1.5 text-gray-500">
                                          {Object.entries(m.permissions ?? {}).filter(([, v]) => v).map(([k]) => PERM_LABELS[k] ?? k).join(", ") || "—"}
                                        </td>
                                        <td class="py-1.5 text-right">
                                          <button onClick={(e) => { e.stopPropagation(); startEditMember(m); }} class="text-xs px-2 py-0.5 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 cursor-pointer">Edit</button>
                                          <Show when={m.role !== "owner"}><button onClick={(e) => { e.stopPropagation(); doRemoveProjectMember(p.id, m.userId); }} class="text-xs px-2 py-0.5 bg-red-900/30 border border-red-700 text-red-300 rounded hover:bg-red-900/50 cursor-pointer ml-1">Remove</button></Show>
                                        </td>
                                      </tr>
                                    )}
                                  </For>
                                </tbody>
                              </table>
                            </Show>
                          </Show>
                          <Show when={showInvitesProject() === p.id && projectInvites()[p.id]}>
                            <h4 class="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-4 mb-2">Pending Invites</h4>
                            <Show when={projectInvites()[p.id].length > 0} fallback={<div class="text-gray-600 text-xs py-2">No pending invites</div>}>
                              <table class="w-full text-xs">
                                <thead class="text-gray-500"><tr><th class="text-left py-1 font-medium">Email</th><th class="text-left py-1 font-medium">Role</th><th class="text-left py-1 font-medium">Created</th><th class="text-right py-1 font-medium">Actions</th></tr></thead>
                                <tbody class="divide-y divide-gray-800/50">
                                  <For each={projectInvites()[p.id]}>
                                    {(inv) => (
                                      <tr>
                                        <td class="py-1.5 text-gray-200">{inv.email}</td>
                                        <td class="py-1.5 text-gray-400 text-xs">{inv.role}</td>
                                        <td class="py-1.5 text-gray-500 text-xs">{new Date(inv.createdAt).toLocaleDateString()}</td>
                                        <td class="py-1.5 text-right space-x-1">
                                          <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(managedJoinUrl(inv.token)); }} class="text-xs px-2 py-0.5 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 cursor-pointer">Copy</button>
                                          <button onClick={(e) => { e.stopPropagation(); doRemoveInvite(p.id, inv.id); }} class="text-xs px-2 py-0.5 bg-red-900/30 border border-red-700 text-red-300 rounded hover:bg-red-900/50 cursor-pointer">Revoke</button>
                                        </td>
                                      </tr>
                                    )}
                                  </For>
                                </tbody>
                              </table>
                            </Show>
                          </Show>
                        </div>
                      </td>
                    </tr>
                  </Show>
                </>
              )}
            </For>
          </tbody>
        </table>
      </div>

      <Show when={totalPages() > 1}>
        <div class="flex items-center justify-center gap-2">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page() === 1} class="px-3 py-1 text-sm bg-gray-800 rounded disabled:opacity-30 cursor-pointer">Prev</button>
          <span class="text-sm text-gray-400">Page {page()} of {totalPages()}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages(), p + 1))} disabled={page() >= totalPages()} class="px-3 py-1 text-sm bg-gray-800 rounded disabled:opacity-30 cursor-pointer">Next</button>
        </div>
      </Show>

      <Show when={editMember()}>
        <EditMemberModal
          member={editMember()!}
          role={editRole()}
          setRole={setEditRole}
          perms={editPerms()}
          setPerms={setEditPerms}
          onSave={saveMember}
          onClose={() => setEditMember(null)}
        />
      </Show>

      <Show when={showCreate()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => setShowCreate(false)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Create Project</h3>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">Name</label><input value={createName()} onInput={(e) => setCreateName(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div><label class="block text-sm text-gray-400 mb-1">Description</label><input value={createDescription()} onInput={(e) => setCreateDescription(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div>
                <label class="block text-sm text-gray-400 mb-1">Project Slug</label>
                <Show
                  when={selectedCreateOrg()}
                  fallback={<input value={createSlug()} onInput={(e) => setCreateSlug(normalizeSegment(e.currentTarget.value))} placeholder="prj1" class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600 font-mono" />}
                >
                  <div class="flex items-center rounded border border-gray-700 bg-gray-800 overflow-hidden">
                    <div class="px-3 py-2 text-sm text-cyan-300 bg-gray-900 border-r border-gray-700 font-mono">{selectedCreateOrg()!.slug}-</div>
                    <input value={createSlug()} onInput={(e) => setCreateSlug(normalizeSegment(e.currentTarget.value))} placeholder="prj1" class="w-full bg-transparent px-3 py-2 text-sm text-gray-200 focus:outline-none font-mono" />
                  </div>
                </Show>
                <div class="mt-1 text-xs text-gray-500">Public project slug: <span class="font-mono text-gray-300">{createProjectFullSlug()}</span></div>
              </div>
              <div class="flex gap-3">
                <div class="flex-1"><label class="block text-sm text-gray-400 mb-1">Visibility</label><select value={createVisibility()} onChange={(e) => setCreateVisibility(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="private">Private</option><option value="unlisted">Unlisted</option><option value="public">Public</option></select></div>
                <div class="flex-1"><label class="block text-sm text-gray-400 mb-1">Block Size</label><input type="number" value={createBlockSize()} onInput={(e) => setCreateBlockSize(parseInt(e.currentTarget.value) || 50)} min={10} max={500} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              </div>
              <div class="flex gap-3">
                <div class="flex-1"><label class="block text-sm text-gray-400 mb-1">Access Mode</label><select value={createAccessMode()} onChange={(e) => setCreateAccessMode(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="open">Open</option><option value="restricted">Restricted</option></select></div>
                <div class="flex-1"><label class="block text-sm text-gray-400 mb-1">Organization</label><select value={createOrganizationId()} onChange={(e) => setCreateOrganizationId(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="">None</option><For each={orgs()}>{(o) => <option value={o.id}>{o.name}</option>}</For></select></div>
              </div>
              <div class="rounded border border-gray-700 bg-gray-950/50 p-3 space-y-3">
                <label class="flex items-center gap-2 text-sm text-gray-300">
                  <input type="checkbox" checked={createTrackingOverrideEnabled()} onChange={(e) => setCreateTrackingOverrideEnabled(e.currentTarget.checked)} class="rounded bg-gray-800 border-gray-700 text-cyan-600" />
                  Override tracking thresholds for this project
                </label>
                <Show when={createTrackingOverrideEnabled()}>
                  <div class="grid gap-3 md:grid-cols-3">
                    <div><label class="block text-sm text-gray-400 mb-1">Distance threshold (m)</label><input type="number" value={createTrackingDistance()} onInput={(e) => setCreateTrackingDistance(parseFloat(e.currentTarget.value) || 0)} min={0} step={0.1} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
                    <div><label class="block text-sm text-gray-400 mb-1">Time threshold (s)</label><input type="number" value={createTrackingTime()} onInput={(e) => setCreateTrackingTime(parseInt(e.currentTarget.value) || 0)} min={0} step={5} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
                    <div><label class="block text-sm text-gray-400 mb-1">Time threshold behavior</label><select value={createTrackingBehavior()} onChange={(e) => setCreateTrackingBehavior(e.currentTarget.value as TrackingSettings["timeThresholdBehavior"])} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600"><option value="live_only">Live update only</option><option value="persist_and_live">Persist and live update</option><option value="no_update">No update</option></select></div>
                  </div>
                </Show>
                <div class="text-xs text-gray-500">Platform default: {trackingSettings().distanceThresholdMeters} m or {trackingSettings().timeThresholdSeconds} s ({trackingSettings().timeThresholdBehavior.replaceAll("_", " ")})</div>
              </div>
              <div>
                <label class="block text-sm text-gray-400 mb-1">Owner</label>
                <select value={createOwnerId()} onChange={(e) => setCreateOwnerId(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer">
                  <option value="">Default</option>
                  <For each={eligibleCreateOwners()}>{(u) => <option value={u.id}>{u.email}{(u as any).role ? ` (${(u as any).role})` : ""}</option>}</For>
                </select>
                <div class="mt-1 text-xs text-gray-500">Only owners and managers can own projects. Searcher-only accounts are excluded.</div>
              </div>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => setShowCreate(false)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={doCreateProject} disabled={creating() || !createName() || !createSlug()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">{creating() ? "Creating..." : "Create"}</button></div>
          </div>
        </div>
      </Show>

      <Show when={showEdit()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => setShowEdit(false)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Edit Project</h3>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">Name</label><input value={editName()} onInput={(e) => setEditName(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div><label class="block text-sm text-gray-400 mb-1">Description</label><input value={editDescription()} onInput={(e) => setEditDescription(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div>
                <label class="block text-sm text-gray-400 mb-1">Project Slug</label>
                <Show
                  when={editProjectPrefix()}
                  fallback={<input value={editSlug()} onInput={(e) => setEditSlug(normalizeSegment(e.currentTarget.value))} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600 font-mono" />}
                >
                  <div class="flex items-center rounded border border-gray-700 bg-gray-800 overflow-hidden">
                    <div class="px-3 py-2 text-sm text-cyan-300 bg-gray-900 border-r border-gray-700 font-mono">{editProjectPrefix()!}-</div>
                    <input value={editSlug()} onInput={(e) => setEditSlug(normalizeSegment(e.currentTarget.value))} class="w-full bg-transparent px-3 py-2 text-sm text-gray-200 focus:outline-none font-mono" />
                  </div>
                </Show>
                <div class="mt-1 text-xs text-gray-500">Public project slug: <span class="font-mono text-gray-300">{editProjectFullSlug()}</span></div>
              </div>
              <div class="flex gap-3">
                <div class="flex-1"><label class="block text-sm text-gray-400 mb-1">Lifecycle</label><select value={editLifecycle()} onChange={(e) => setEditLifecycle(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="draft">Draft</option><option value="published">Published</option><option value="active">Active</option><option value="paused">Paused</option><option value="closed">Closed</option><option value="archived">Archived</option></select></div>
                <div class="flex-1"><label class="block text-sm text-gray-400 mb-1">Block Size</label><input type="number" value={editBlockSize()} onInput={(e) => setEditBlockSize(parseInt(e.currentTarget.value) || 50)} min={10} max={500} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              </div>
              <div class="flex gap-3">
                <div class="flex-1"><label class="block text-sm text-gray-400 mb-1">Visibility</label><select value={editVisibility()} onChange={(e) => setEditVisibility(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="private">Private</option><option value="unlisted">Unlisted</option><option value="public">Public</option></select></div>
                <div class="flex-1"><label class="block text-sm text-gray-400 mb-1">Access Mode</label><select value={editAccessMode()} onChange={(e) => setEditAccessMode(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="open">Open</option><option value="restricted">Restricted</option></select></div>
              </div>
              <div class="rounded border border-gray-700 bg-gray-950/50 p-3 space-y-3">
                <label class="flex items-center gap-2 text-sm text-gray-300">
                  <input type="checkbox" checked={editTrackingOverrideEnabled()} onChange={(e) => setEditTrackingOverrideEnabled(e.currentTarget.checked)} class="rounded bg-gray-800 border-gray-700 text-cyan-600" />
                  Override tracking thresholds for this project
                </label>
                <Show when={editTrackingOverrideEnabled()}>
                  <div class="grid gap-3 md:grid-cols-3">
                    <div><label class="block text-sm text-gray-400 mb-1">Distance threshold (m)</label><input type="number" value={editTrackingDistance()} onInput={(e) => setEditTrackingDistance(parseFloat(e.currentTarget.value) || 0)} min={0} step={0.1} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
                    <div><label class="block text-sm text-gray-400 mb-1">Time threshold (s)</label><input type="number" value={editTrackingTime()} onInput={(e) => setEditTrackingTime(parseInt(e.currentTarget.value) || 0)} min={0} step={5} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
                    <div><label class="block text-sm text-gray-400 mb-1">Time threshold behavior</label><select value={editTrackingBehavior()} onChange={(e) => setEditTrackingBehavior(e.currentTarget.value as TrackingSettings["timeThresholdBehavior"])} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600"><option value="live_only">Live update only</option><option value="persist_and_live">Persist and live update</option><option value="no_update">No update</option></select></div>
                  </div>
                </Show>
                <div class="text-xs text-gray-500">Platform default: {trackingSettings().distanceThresholdMeters} m or {trackingSettings().timeThresholdSeconds} s ({trackingSettings().timeThresholdBehavior.replaceAll("_", " ")})</div>
              </div>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => setShowEdit(false)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={doUpdateProject} disabled={editing()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">{editing() ? "Saving..." : "Save"}</button></div>
          </div>
        </div>
      </Show>

      {/* Add Member Modal */}
      <Show when={showAddMember()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => setShowAddMember(false)} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Add Member</h3>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">User</label><select value={addMemberUserId()} onChange={(e) => setAddMemberUserId(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="">Select user...</option><For each={users()}>{(u) => <option value={u.id}>{u.email}</option>}</For></select></div>
              <div><label class="block text-sm text-gray-400 mb-1">Role</label><select value={addMemberRole()} onChange={(e) => setAddMemberRole(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="manager">Manager</option><option value="searcher">Searcher</option><option value="owner">Owner</option></select></div>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => setShowAddMember(false)} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button><button onClick={doAddMember} disabled={!addMemberUserId()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">Add</button></div>
          </div>
        </div>
      </Show>

      {/* Invite Modal */}
      <Show when={showInvite()}>
        <div class="fixed inset-0 z-50 flex items-center justify-center"><div class="absolute inset-0 bg-black/60" onClick={() => { setShowInvite(false); setInviteResult(null); }} />
          <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
            <h3 class="text-lg font-semibold mb-4">Invite to Project</h3>
            <div class="space-y-4">
              <div><label class="block text-sm text-gray-400 mb-1">Email</label><input value={inviteEmail()} onInput={(e) => setInviteEmail(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 focus:outline-none focus:border-cyan-600" /></div>
              <div><label class="block text-sm text-gray-400 mb-1">Role</label><select value={inviteRole()} onChange={(e) => setInviteRole(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer"><option value="manager">Manager</option><option value="searcher">Searcher</option></select></div>
              <Show when={inviteResult()}>
                <div>
                  <div class="bg-gray-800 rounded px-3 py-2 text-xs text-gray-300 break-all mb-3 font-mono">{inviteResult()!.link}</div>
                  <button onClick={() => { navigator.clipboard.writeText(inviteResult()!.link); }} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 cursor-pointer w-full">Copy Link</button>
                </div>
              </Show>
            </div>
            <div class="flex justify-end gap-3 pt-4"><button onClick={() => { setShowInvite(false); setInviteResult(null); }} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Close</button><button onClick={doCreateInvite} disabled={!inviteEmail()} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 cursor-pointer">{inviteResult() ? "Generate Another" : "Generate Invite"}</button></div>
          </div>
        </div>
      </Show>
    </div>
  );
}

function EditMemberModal(props: {
  member: ProjectMember;
  role: string;
  setRole: (r: string) => void;
  perms: Record<string, boolean>;
  setPerms: (p: Record<string, boolean>) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  return (
    <div class="fixed inset-0 z-50 flex items-center justify-center">
      <div class="absolute inset-0 bg-black/60" onClick={props.onClose} />
      <div class="relative bg-gray-900 border border-gray-700 rounded-lg p-6 w-full max-w-sm shadow-xl z-10">
        <h3 class="text-lg font-semibold mb-1">Edit Member</h3>
        <p class="text-sm text-gray-400 mb-4">{props.member.userEmail}</p>
        <div class="space-y-4">
          <div>
            <label class="block text-sm text-gray-400 mb-1">Role</label>
            <select value={props.role} onChange={(e) => props.setRole(e.currentTarget.value)} class="w-full bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-gray-200 cursor-pointer">
              <option value="owner">Owner</option>
              <option value="manager">Manager</option>
              <option value="searcher">Searcher</option>
            </select>
          </div>
          <div>
            <label class="block text-sm text-gray-400 mb-2">Permissions</label>
            <div class="space-y-2">
              <For each={Object.entries(PERM_LABELS)}>
                {([key, label]) => (
                  <label class="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={props.perms[key] ?? false}
                      onChange={(e) => props.setPerms({ ...props.perms, [key]: e.currentTarget.checked })}
                      class="rounded bg-gray-800 border-gray-700 text-cyan-600 focus:ring-cyan-600"
                    />
                    <span class="text-sm text-gray-300">{label}</span>
                  </label>
                )}
              </For>
            </div>
          </div>
        </div>
        <div class="flex justify-end gap-3 pt-4">
          <button onClick={props.onClose} class="px-4 py-2 text-sm text-gray-400 hover:text-white cursor-pointer">Cancel</button>
          <button onClick={props.onSave} class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 cursor-pointer">Save</button>
        </div>
      </div>
    </div>
  );
}

function LifecycleBadge(props: { lifecycle: string }) {
  const colors: Record<string, string> = {
    draft: "bg-gray-800 text-gray-400 border-gray-700",
    published: "bg-yellow-900/40 text-yellow-300 border-yellow-700",
    active: "bg-emerald-900/40 text-emerald-300 border-emerald-700",
    paused: "bg-amber-900/40 text-amber-300 border-amber-700",
    closed: "bg-red-900/40 text-red-300 border-red-700",
    archived: "bg-purple-900/40 text-purple-300 border-purple-700",
    planning: "bg-gray-800 text-gray-400 border-gray-700",
    completed: "bg-blue-900/40 text-blue-300 border-blue-700",
  };
  return <span class={`px-2 py-0.5 text-xs font-medium rounded border ${colors[props.lifecycle] ?? colors.planning}`}>{props.lifecycle}</span>;
}

function RoleBadge(props: { role: string }) {
  const colors: Record<string, string> = {
    owner: "bg-purple-900/40 text-purple-300 border-purple-700",
    manager: "bg-blue-900/40 text-blue-300 border-blue-700",
  };
  return <span class={`px-2 py-0.5 text-xs font-medium rounded border ${colors[props.role] ?? "bg-gray-800 text-gray-400 border-gray-700"}`}>{props.role}</span>;
}

function StatusBadge(props: { status: string }) {
  const colors: Record<string, string> = {
    approved: "bg-emerald-900/40 text-emerald-300 border-emerald-700",
    pending: "bg-amber-900/40 text-amber-300 border-amber-700",
    declined: "bg-red-900/40 text-red-300 border-red-700",
  };
  return <span class={`px-2 py-0.5 text-xs font-medium rounded border ${colors[props.status] ?? colors.pending}`}>{props.status}</span>;
}
